import { ChevronDown } from 'lucide-react'
import { motion, type Transition, useReducedMotion } from 'motion/react'
import { type ReactNode, useCallback, useId, useLayoutEffect, useRef, useState } from 'react'

import { cn } from '../lib/utils'

// Ported from beUI's "Bouncy Accordion" (https://beui.dev/components/motion/bouncy-accordion),
// a shadcn-style copy-paste component (there is no npm package — the source is meant to be
// copied in). The structure and motion — connected-group corner morphing, gap separation, and
// the spring physics on row position, content height, and chevron — are kept as authored there.
// What we changed: colors are swapped for Parsley design-system tokens, a brand focus ring is
// added, and the public item field is `content` (not `description`). beUI notes its components
// are recreated from existing work across the web and ships them to be copied; credited here
// per that intent.

// Strong ease-out for the description fade (beUI's EASE_OUT token).
const EASE_OUT = [0.16, 1, 0.3, 1] as const

// Corner radius of a connected group's outer corners. beUI uses 28px; kept here as one knob.
const GROUP_RADIUS = 28

export type AccordionItem = {
  /** Stable id — also the value reported by `onValueChange`. */
  id: string
  title: ReactNode
  content?: ReactNode
  /** Optional leading icon on the trigger row. */
  icon?: ReactNode
  disabled?: boolean
}

export type AccordionClassNames = {
  root?: string
  item?: string
  trigger?: string
  icon?: string
  title?: string
  chevron?: string
  content?: string
  description?: string
}

/**
 * How the rows behave as a set. `grouped` is the connected-group treatment: rows fuse, morph
 * their corners, and separate with a gap on a bounced spring. `calm` holds the stack still —
 * static corners, hairline dividers, and only the panel height moves — for surfaces where the
 * row is a means to a form rather than the thing being read.
 */
export type AccordionVariant = 'grouped' | 'calm'

export interface AccordionProps {
  items: AccordionItem[]
  /** Controlled open id (`null` = all closed). Omit for uncontrolled. */
  value?: string | null
  /** Uncontrolled initial open id. Ignored when `value` is provided. */
  defaultValue?: string | null
  onValueChange?: (value: string | null) => void
  /** Whether clicking the open item closes it (all-collapsed state). Default `true`. */
  collapsible?: boolean
  /** Row-set behaviour. Default `grouped`. */
  variant?: AccordionVariant
  className?: string
  classNames?: AccordionClassNames
}

// Local springs keep the accordion's connected groups moving together while
// avoiding scale projection on text-heavy row contents.
// Gap spring: must not overshoot y — positive y overshoot drifts items below
// their resting point and briefly overlaps the next item.
const ROW_TRANSITION: Transition = {
  type: 'spring',
  duration: 0.55,
  bounce: 0.38
}

const CONTENT_OPEN_TRANSITION: Transition = {
  type: 'spring',
  duration: 0.58,
  bounce: 0.32
}

const CONTENT_CLOSE_TRANSITION: Transition = {
  type: 'spring',
  duration: 0.46,
  bounce: 0.26
}

const DESCRIPTION_TRANSITION: Transition = {
  duration: 0.18,
  ease: EASE_OUT
}

const CHEVRON_TRANSITION: Transition = {
  type: 'spring',
  duration: 0.42,
  bounce: 0.28
}

// `calm` counterparts: eased, no overshoot. Nothing but the panel height and the chevron moves,
// so a row opening next to a text field doesn't pull the eye off it.
const CALM_CONTENT_OPEN_TRANSITION: Transition = { duration: 0.4, ease: EASE_OUT }
const CALM_CONTENT_CLOSE_TRANSITION: Transition = { duration: 0.36, ease: EASE_OUT }
const CALM_CHEVRON_TRANSITION: Transition = { duration: 0.18, ease: EASE_OUT }

function useControllableAccordionValue({
  value,
  defaultValue,
  onValueChange
}: {
  value?: string | null
  defaultValue?: string | null
  onValueChange?: (value: string | null) => void
}) {
  const [internalValue, setInternalValue] = useState(defaultValue ?? null)
  const isControlled = value !== undefined
  // Branch on control, not on nullishness: `value ?? internalValue` sent a controlled `null`
  // (all rows closed) falling through to the uncontrolled state instead of closing them.
  const currentValue = isControlled ? (value ?? null) : internalValue

  const setValue = useCallback(
    (next: string | null) => {
      if (!isControlled) {
        setInternalValue(next)
      }

      onValueChange?.(next)
    },
    [isControlled, onValueChange]
  )

  return [currentValue, setValue] as const
}

function AccordionRow({
  item,
  open,
  variant,
  startsGroup,
  endsGroup,
  separatedFromPrevious,
  isLast,
  contentId,
  triggerId,
  reduce,
  classNames,
  onToggle
}: {
  item: AccordionItem
  open: boolean
  variant: AccordionVariant
  startsGroup: boolean
  endsGroup: boolean
  separatedFromPrevious: boolean
  isLast: boolean
  contentId: string
  triggerId: string
  reduce: boolean | null
  classNames?: AccordionClassNames
  onToggle: () => void
}) {
  const calm = variant === 'calm'
  const contentRef = useRef<HTMLDivElement>(null)
  const regionRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [contentHeight, setContentHeight] = useState(0)

  // A collapsed row is height:0 + overflow:hidden, which hides its content visually but leaves
  // anything focusable inside it IN THE TAB ORDER — so tabbing off a collapsed header dropped
  // focus into invisible content instead of moving to the next header. `aria-hidden` alone did
  // not help (it hides from AT but not from Tab, and aria-hidden wrapping focusable content is
  // itself invalid). `inert` removes the subtree from both. Set imperatively because React 18
  // silently drops a JSX `inert` prop — it landed in React 19. Same approach as GooeyPopoverContent.
  useLayoutEffect(() => {
    const region = regionRef.current
    if (!region) return
    // Going inert while focus is inside would strand it on <body>; hand it back to the trigger
    // that owns this region first.
    if (!open && region.contains(document.activeElement)) triggerRef.current?.focus()
    region.toggleAttribute('inert', !open)
  }, [open])

  useLayoutEffect(() => {
    const node = contentRef.current
    if (!node) return

    const updateHeight = () => {
      setContentHeight(node.offsetHeight)
    }

    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(node)

    return () => {
      observer.disconnect()
    }
  }, [])

  const body = (
    <>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        disabled={item.disabled}
        aria-expanded={open}
        aria-controls={contentId}
        onClick={onToggle}
        // Mirror whichever of the row's corners are rounded. The ring is INSET (an outward one
        // would be clipped away entirely by the row's overflow-hidden), and an inset ring traces
        // this button's own border box — so with square corners its ends were being cut off by
        // the row's rounded clip. Driven off GROUP_RADIUS rather than a Tailwind literal so the
        // two can't drift. Only the TOP corners follow `startsGroup`: when the row is open the
        // content sits below this button, so the row's bottom corners are not the button's.
        // `calm` rows are square throughout — the card wrapping them owns the radius.
        style={
          calm
            ? undefined
            : {
                borderTopLeftRadius: startsGroup ? GROUP_RADIUS : 0,
                borderTopRightRadius: startsGroup ? GROUP_RADIUS : 0,
                borderBottomLeftRadius: endsGroup && !open ? GROUP_RADIUS : 0,
                borderBottomRightRadius: endsGroup && !open ? GROUP_RADIUS : 0
              }
        }
        className={cn(
          'flex min-h-[54px] w-full items-center gap-4 px-5 text-left outline-none transition-colors',
          'focus-visible:bg-muted/25 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300',
          'disabled:pointer-events-none',
          // The row's px-2 pulls this in from the card edge so the ring and the hover fill read as
          // a rounded bar rather than a square band butting the card. px-3 puts the label back at
          // the same 20px the divider and the neighbouring rows use.
          calm && 'rounded-lg px-3',
          classNames?.trigger
        )}>
        {item.icon ? (
          <span className={cn('grid h-7 w-7 shrink-0 place-items-center text-muted-foreground', classNames?.icon)}>{item.icon}</span>
        ) : null}
        <span className={cn('min-w-0 flex-1 truncate text-base font-medium text-foreground', classNames?.title)}>{item.title}</span>
        <motion.span
          aria-hidden
          animate={{ rotate: open ? 180 : 0 }}
          transition={reduce ? { duration: 0 } : calm ? CALM_CHEVRON_TRANSITION : CHEVRON_TRANSITION}
          className={cn('grid h-6 w-6 shrink-0 place-items-center text-muted-foreground', classNames?.chevron)}>
          <ChevronDown className="h-4 w-4" />
        </motion.span>
      </button>

      <motion.div
        ref={regionRef}
        // Layout projection is what lets an opening row push its neighbours on a spring, and in
        // `grouped` it is also what animates this height — the style below is only its target.
        // `calm` drops projection to hold the stack still, so it has to animate height itself.
        layout={calm ? undefined : 'size'}
        id={contentId}
        role="region"
        aria-labelledby={triggerId}
        aria-hidden={!open}
        initial={false}
        animate={calm ? { height: open && item.content ? contentHeight : 0 } : undefined}
        style={calm ? undefined : { height: open && item.content ? contentHeight : 0 }}
        transition={
          reduce
            ? { duration: 0 }
            : calm
              ? open
                ? CALM_CONTENT_OPEN_TRANSITION
                : CALM_CONTENT_CLOSE_TRANSITION
              : open
                ? CONTENT_OPEN_TRANSITION
                : CONTENT_CLOSE_TRANSITION
        }
        className={cn('overflow-hidden', classNames?.content)}>
        <motion.div
          ref={contentRef}
          animate={{ opacity: open ? 1 : 0 }}
          transition={reduce ? { duration: 0 } : DESCRIPTION_TRANSITION}
          className="px-5 pb-5">
          <div className={cn('text-base leading-6 text-muted-foreground', classNames?.description)}>{item.content}</div>
        </motion.div>
      </motion.div>
    </>
  )

  // No gap, no corner morph, no layout projection — the row is a plain div, and whatever card the
  // caller wraps this in supplies the radius and the clip. The divider is an inset rule rather
  // than a border-b so the row stays full-bleed: its hover and focus fill reach the card's edges
  // while the line starts at the same gutter as the label. `inset-x-5` tracks the trigger's px-5.
  if (calm) {
    return (
      <div
        data-state={open ? 'open' : 'closed'}
        className={cn(
          'relative bg-card px-2 text-card-foreground',
          // `pb-px` gives the rule its own pixel row. Without it the rule lands on the trigger's
          // bottom edge and, painting later as a positioned pseudo-element, clips 1px off the
          // inset focus ring — the ring reads as chopped along the bottom.
          !isLast && "pb-px after:absolute after:inset-x-5 after:bottom-0 after:h-px after:bg-border after:content-['']",
          item.disabled && 'opacity-50',
          classNames?.item
        )}>
        {body}
      </div>
    )
  }

  return (
    <motion.div
      layout="position"
      initial={false}
      style={{ marginTop: separatedFromPrevious ? 12 : 0 }}
      transition={reduce ? { duration: 0 } : ROW_TRANSITION}>
      <motion.div
        data-state={open ? 'open' : 'closed'}
        initial={false}
        animate={{
          borderTopLeftRadius: startsGroup ? GROUP_RADIUS : 0,
          borderTopRightRadius: startsGroup ? GROUP_RADIUS : 0,
          borderBottomLeftRadius: endsGroup ? GROUP_RADIUS : 0,
          borderBottomRightRadius: endsGroup ? GROUP_RADIUS : 0
        }}
        transition={reduce ? { duration: 0 } : ROW_TRANSITION}
        className={cn('overflow-hidden bg-card text-card-foreground', item.disabled && 'opacity-50', classNames?.item)}>
        {body}
      </motion.div>
    </motion.div>
  )
}

/**
 * Single-open accordion — one panel expands at a time. Generic: items in, open-id out, no
 * feature semantics. Controlled (pass `value` + `onValueChange`) or uncontrolled
 * (`defaultValue`). Each trigger is a real `<button aria-expanded>` driving an
 * `aria-labelledby` region, and a collapsed region is `inert` so Tab skips its contents.
 * `prefers-reduced-motion` makes everything instant in both variants.
 *
 * `variant="grouped"` (default) is the bouncy reveal: adjacent items connect into a rounded
 * group, and opening one separates it with a gap and morphs the group's corners on springs.
 * Right when the panel contents are the thing being read — the Settings → Help FAQ.
 *
 * `variant="calm"` holds the stack still: static square corners, hairline dividers, and only
 * the panel height and chevron move. Right when the row is a means to something else, like a
 * form — Settings → Profile. Wrap it in your own card; that card owns the radius and the clip.
 */
export function Accordion({
  items,
  value,
  defaultValue = null,
  onValueChange,
  collapsible = true,
  variant = 'grouped',
  className,
  classNames
}: AccordionProps) {
  const reduce = useReducedMotion()
  const baseId = useId()
  const [activeValue, setActiveValue] = useControllableAccordionValue({
    value,
    defaultValue,
    onValueChange
  })
  const activeIndex = items.findIndex((item) => item.id === activeValue)

  const toggleItem = useCallback(
    (id: string) => {
      if (activeValue === id) {
        if (collapsible) {
          setActiveValue(null)
        }
        return
      }

      setActiveValue(id)
    },
    [activeValue, collapsible, setActiveValue]
  )

  return (
    <div className={cn('w-full', className, classNames?.root)}>
      {items.map((item, index) => {
        const open = activeValue === item.id
        const previousIsOpen = activeIndex === index - 1
        const nextIsOpen = activeIndex === index + 1
        const startsGroup = open || index === 0 || previousIsOpen
        const endsGroup = open || index === items.length - 1 || nextIsOpen
        const separatedFromPrevious = index > 0 && (open || previousIsOpen)
        const contentId = `${baseId}-${item.id}-content`
        const triggerId = `${baseId}-${item.id}-trigger`

        return (
          <AccordionRow
            key={item.id}
            item={item}
            open={open}
            variant={variant}
            isLast={index === items.length - 1}
            startsGroup={startsGroup}
            endsGroup={endsGroup}
            separatedFromPrevious={separatedFromPrevious}
            contentId={contentId}
            triggerId={triggerId}
            reduce={reduce}
            classNames={classNames}
            onToggle={() => toggleItem(item.id)}
          />
        )
      })}
    </div>
  )
}
